import { useQuery } from '@tanstack/react-query';
import { listLeadStagesV2 } from 'services/Settings';
import { queryKeys } from 'hooks/config/queryClient';
import { queryKeys as leadBoardQueryKeys } from 'modules/lead/LeadsBoard/config/queryClient';
import { queryKeys as leadDropdownQueryKeys } from 'modules/cases/config/queryClient';

/**
 * @param {*} response — axios response from `listLeadStagesV2`
 * @param {number} fallbackPage
 * @param {number} fallbackLimit
 */
function mapLeadStagesV2Response(response, fallbackPage, fallbackLimit) {
  const raw = response?.data ?? {};
  const list = raw.lead_stage ?? raw['lead-stage'] ?? raw.data ?? raw.items ?? [];
  const rows = Array.isArray(list) ? list : [];
  const totalCount = Number(raw.total_count ?? raw?.pagination?.total_count ?? rows.length) || 0;
  const apiPage = Number(raw.page) > 0 ? Number(raw.page) : fallbackPage;
  const apiLimit = Number(raw.limit) > 0 ? Number(raw.limit) : fallbackLimit;
  const hasNext = raw.has_next != null ? Boolean(raw.has_next) : apiPage * apiLimit < totalCount;
  const hasPrevious = raw.has_previous != null ? Boolean(raw.has_previous) : apiPage > 1;

  return { rows, totalCount, page: apiPage, limit: apiLimit, hasNext, hasPrevious };
}

export const fetchLeadStagesListV2 = async ({
  excludeFromLoading = false,
  limit = 20,
  page = 1,
  sort_by = '',
  sort_order = '',
  keyword = '',
  lead_stage_status = '',
  practice_area,
  show_unassigned_pa_column,
}) => {
  // Mirror the previous /list-lead-stages body behavior: only attach keys that the caller actually provided.
  // Settings page sends no body; LeadContext / board send `{ practice_area, show_unassigned_pa_column }`.
  const body = {};
  if (practice_area != null) {
    body.practice_area = practice_area;
  }
  if (show_unassigned_pa_column != null) {
    body.show_unassigned_pa_column = show_unassigned_pa_column;
  }
  const data = Object.keys(body).length ? body : null;

  const response = await listLeadStagesV2({
    excludeFromLoading,
    limit,
    page,
    sort_by,
    sort_order,
    keyword,
    lead_stage_status,
    data,
  });
  return mapLeadStagesV2Response(response, page, limit);
};

const DROPDOWN_PREFETCH_STALE_MS = 5 * 60 * 1000;
const DROPDOWN_PREFETCH_GC_MS = 10 * 60 * 1000;

/**
 * Prefetch the first page of lead stages for the same React Query key used by
 * `LeadStageSelect` / `AsyncPaginate` (automation practice-area change, etc.).
 *
 * @param {import('@tanstack/react-query').QueryClient} queryClient
 * @param {Array|null|undefined} practice_area — V2 body `practice_area` (raw IDs or `practice_area#...`; see `extractPracticeAreaIds`)
 * @param {Object} [overrides]
 * @param {string} [overrides.keyword='']
 * @param {number} [overrides.page=1]
 * @param {number} [overrides.pageSize=20]
 * @param {string} [overrides.lead_stage_status='ACTIVE']
 * @param {string} [overrides.sort_by='lead_stage_name']
 * @param {string} [overrides.sort_order='asc']
 * @param {boolean} [overrides.show_unassigned_pa_column=true]
 * @returns {Promise<ReturnType<typeof mapLeadStagesV2Response>>}
 */
/** Mark lead-stages-v2 caches stale when practice area scope changes (no immediate refetch). */
export function invalidateLeadStagesV2OnPracticeAreaChange(queryClient) {
  return queryClient.invalidateQueries({
    queryKey: queryKeys.leadStagesV2.all,
    refetchType: 'none',
  });
}

export function prefetchLeadStagesV2Dropdown(queryClient, practice_area, overrides = {}) {
  const {
    keyword = '',
    page = 1,
    pageSize = 20,
    lead_stage_status = 'ACTIVE',
    sort_by = 'lead_stage_name',
    sort_order = 'asc',
    show_unassigned_pa_column = true,
  } = overrides;

  return queryClient.fetchQuery({
    queryKey: queryKeys.leadStagesV2.dropdown(keyword, page, pageSize, lead_stage_status, sort_by, sort_order, practice_area),
    queryFn: () =>
      fetchLeadStagesListV2({
        excludeFromLoading: true,
        limit: pageSize,
        page,
        sort_by,
        sort_order,
        keyword,
        lead_stage_status,
        practice_area,
        show_unassigned_pa_column,
      }),
    staleTime: DROPDOWN_PREFETCH_STALE_MS,
    gcTime: DROPDOWN_PREFETCH_GC_MS,
  });
}

/**
 * Invalidate all lead-stages-v2 queries plus the legacy leads board / dropdown caches
 * so create/update/archive/reorder mutations refresh every dependent view.
 *
 * @param {import('@tanstack/react-query').QueryClient} queryClient
 */
export const invalidateLeadStagesV2 = (queryClient) =>
  Promise.all([
    queryClient.invalidateQueries({
      queryKey: queryKeys.leadStagesV2.all,
      refetchType: 'active',
    }),
    queryClient.invalidateQueries({ queryKey: leadBoardQueryKeys.leadStages.all }),
    queryClient.invalidateQueries({ queryKey: leadBoardQueryKeys.leads.all }),
    queryClient.invalidateQueries({ queryKey: leadDropdownQueryKeys.leadStages.all }),
  ]);

/**
 * Paginated lead stages (`list-lead-stages-v2`). Same shape as BillingV2 list hooks:
 * callers pass API `sort_by` / `sort_order` (both `''` on first load; set when the user sorts).
 *
 * @param {Object} [opts]
 * @param {number} [opts.limit=20]
 * @param {number} [opts.page=1]
 * @param {string} [opts.sort_by=''] — forwarded to API (e.g. `lead_stage_name`)
 * @param {string} [opts.sort_order=''] — forwarded to API (`asc` or `desc`)
 * @param {string} [opts.keyword=''] — keyword search (forwarded to API)
 * @param {string} [opts.lead_stage_status=''] — `ACTIVE` / `ARCHIVED` filter
 * @param {Array|null} [opts.practice_area=null] — body filter
 * @param {boolean} [opts.show_unassigned_pa_column]
 * @param {boolean} [opts.enabled=true]
 * @param {boolean} [opts.excludeFromLoading=false]
 */
export function useLeadStagesListV2Query({
  limit = 20,
  page = 1,
  sort_by = '',
  sort_order = '',
  keyword = '',
  lead_stage_status = '',
  practice_area,
  show_unassigned_pa_column,
  enabled = true,
  excludeFromLoading = false,
} = {}) {
  // Normalize undefined ⇄ null to a single sentinel for cache stability.
  const filters = {
    sort_by: sort_by ?? '',
    sort_order: sort_order ?? '',
    keyword: keyword ?? '',
    lead_stage_status: lead_stage_status ?? '',
    practice_area: practice_area ?? null,
    show_unassigned_pa_column: show_unassigned_pa_column ?? null,
  };

  const queryKey = queryKeys.leadStagesV2.listV2(filters, page, limit);

  const query = useQuery({
    queryKey,
    queryFn: () =>
      fetchLeadStagesListV2({
        excludeFromLoading,
        limit,
        page,
        sort_by: filters.sort_by,
        sort_order: filters.sort_order,
        keyword: filters.keyword,
        lead_stage_status: filters.lead_stage_status,
        // Pass through; the fetcher drops null/undefined keys from the body.
        practice_area,
        show_unassigned_pa_column,
      }),
    enabled,
    keepPreviousData: true,
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
    retry: 2,
  });

  const data = query.data;

  return {
    rows: data?.rows ?? [],
    totalCount: data?.totalCount ?? 0,
    hasNext: data?.hasNext ?? false,
    hasPrevious: data?.hasPrevious ?? false,
    apiPage: data?.page ?? page,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
    refetch: query.refetch,
  };
}
